// Story 6.1 (ADO-877) — Auth0 client factory + session helpers (AC1, AC2, AC5).
//
// Public client: Authorization Code + PKCE (S256 — the SDK default and the only
// code_challenge_method this configuration emits). There is NO client secret in
// the browser (AC1).
//
// Token storage is `localStorage` — the Bug #1192 (ADO-1192) decision of
// record, REVISING OQ-6.3's original in-memory posture: the in-memory cache
// discarded the rotating refresh token on every hard reload, and with the SDK's
// `useRefreshTokensFallback: false` default the post-reload `getTokenSilently`
// threw `missing_refresh_token` without attempting any restore — a hard refresh
// silently signed the user out (and the iframe fallback could never work in the
// Capacitor webviews, where third-party cookies are blocked).
// `useRefreshTokens: true` requests `offline_access` and uses a rotating refresh
// token (rotation + TTLs are Deloitte-owned Auth0 application settings) instead
// of hidden-iframe silent auth. The persisted-cache XSS trade-off is mitigated
// by short access TTL + refresh rotation/reuse-detection (Deloitte-owned,
// load-bearing — see the Bug #1192 story, OQ-1192.1/OQ-1192.2).
//
// This module does NOT mint, derive, or trust-decide on the token's
// `shoplink/*` claims — `apps/api` is the authority (AUTH_FLOW_SPEC §3). The SPA
// only reads/forwards the bearer token.

import { type Auth0Client, type IdToken, createAuth0Client } from '@auth0/auth0-spa-js';
import type { Auth0RuntimeConfig } from './auth-config';

/**
 * True when `window.localStorage` is actually usable. Under Chrome/Edge "Block
 * all cookies" (and some storage-partitioned webviews) the `localStorage`
 * GETTER itself throws SecurityError — and the SDK's LocalStorageCache reads it
 * unguarded, which would otherwise turn the very first cache read into a
 * bootstrap-failing rejection. Exported for tests.
 */
export function isLocalStorageUsable(): boolean {
  try {
    const probe = '__shoplink_ls_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * Create a configured Auth0 SPA client. Public-client + PKCE + persisted cache +
 * rotating refresh token. `audience` in `authorizationParams` drives a
 * claim-carrying access token. Story 6.14 (ADO-1186, D2): Auth0 Organizations
 * are not used — no `organization` param is ever sent on `/authorize`; login
 * branding is Auth0-tenant-wide.
 */
export function createClient(config: Auth0RuntimeConfig): Promise<Auth0Client> {
  return createAuth0Client({
    domain: config.domain,
    clientId: config.clientId,
    // Rotating refresh token (offline_access) instead of iframe silent auth.
    useRefreshTokens: true,
    // Bug #1192 (ADO-1192) — persist the cache so the rotating refresh token
    // survives a hard reload; `useRefreshTokensFallback` stays at its `false`
    // default (no third-party-cookie iframe path). Revises OQ-6.3.
    // Where localStorage itself is blocked (throws on access), fall back to the
    // pre-1192 in-memory posture so the shell still boots. Note the honest
    // limit: under "Block all cookies" sessionStorage (the SDK's transaction
    // store) usually throws too, so LOGIN may still fail for those users —
    // identical to pre-1192; the fallback only guarantees a working signed-out
    // shell instead of a bootstrap error screen.
    cacheLocation: isLocalStorageUsable() ? 'localstorage' : 'memory',
    authorizationParams: {
      redirect_uri: window.location.origin,
      audience: config.audience,
      // Story 6.32 (ADO-1315) — the BWG integration guide pins this exact scope
      // set. Behaviour-neutral: the SDK already requests `openid profile email`
      // by default and `useRefreshTokens: true` adds `offline_access`; making it
      // explicit turns the documented contract into reviewable code.
      scope: 'openid profile email offline_access',
    },
  });
}

/**
 * Bug #1192 review — purge the client's PERSISTED session locally, without
 * navigating. `logout({ openUrl: false })` is the SDK's own idiom for a
 * local-only clear (it clears the cache + is-authenticated cookie first and
 * skips the redirect): used when a session is discovered DEAD (terminal
 * refresh failure) so the revoked token material cannot linger in localStorage
 * and rehydrate a dead-session UI on the next load. Best-effort by design —
 * callers treat failures as non-fatal.
 */
export async function clearPersistedSession(client: Auth0Client): Promise<void> {
  try {
    await client.logout({ openUrl: false });
  } catch {
    /* best-effort: an unusable storage layer means nothing persisted anyway */
  }
}

/**
 * Story 6.27 (ADO-1206) — true only for a SAFE same-origin return path: a
 * non-empty string beginning with a single `/` and NOT `//` or `/\` (the
 * protocol-relative and backslash open-redirect vectors browsers normalise to a
 * cross-origin destination). A threaded `returnTo` that passes this can only ever
 * navigate WITHIN the SPA, never off-origin.
 *
 * Used at BOTH ends of the deep-link round-trip — the sign-in initiator (capture)
 * and {@link completeRedirectCallback} (consume) — so the open-redirect guard
 * cannot drift between them. `appState` is SDK-local (auth0-spa-js persists it in
 * its own transaction store, it is NEVER sent to or echoed back by the Auth0
 * server), so it is not attacker-supplied via the redirect; the callback still
 * validates it un-trusted here as defence-in-depth.
 */
export function isSafeReturnTo(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    // Reject ANY backslash (browsers normalise `\` → `/`, so `/\` and any embedded
    // backslash are protocol-relative / cross-origin vectors) and ANY C0/DEL
    // control char — a raw tab/newline/CR is stripped during URL parsing, which
    // can re-expose a leading `//`. This makes the predicate honour its contract
    // ("can only ever navigate WITHIN the SPA") even if it is later reused for a
    // `window.location`/`<a href>` sink, not just react-router `navigate`.
    !containsUnsafeChar(value)
  );
}

/**
 * Story 6.27 (ADO-1206) — code-point scan for the chars {@link isSafeReturnTo}
 * forbids: ANY backslash (browsers normalise `\` -> `/`, a protocol-relative /
 * cross-origin vector) and ANY C0/DEL control char (a raw tab/newline/CR is
 * stripped during URL parsing, which can re-expose a leading `//`). A loop, not a
 * control-char regex literal, which would trip `no-control-regex`.
 */
function containsUnsafeChar(value: string): boolean {
  for (const ch of value) {
    const code = ch.charCodeAt(0);
    if (ch === '\\' || code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * Begin sign-in by redirecting to Universal Login (AC2). Story 6.14 (ADO-1186,
 * D9): this is the SINGLE sign-in entry — staff are custom-DB users on the same
 * shared connection (`sub = auth0|<uuid>`) and sign in through this exact flow;
 * role/tenant derive from claims. The former enterprise-connection staff
 * initiator (`signInStaff`, Story 6.2) is descoped from Phase 1.
 *
 * Story 6.27 (ADO-1206) — when the user was deep-linked to a protected route and
 * bounced to sign-in, `returnTo` (the intended in-app path) is threaded to Auth0
 * as `appState.returnTo`. auth0-spa-js round-trips `appState` through its own
 * transaction store and hands it back from `handleRedirectCallback` (see
 * {@link completeRedirectCallback}) so the shell can restore that route after the
 * callback. Only a {@link isSafeReturnTo}-valid path is forwarded; with no (or an
 * unsafe) `returnTo` the call is byte-for-byte the pre-6.27 `loginWithRedirect()`
 * and the post-login landing stays the origin.
 */
export function signIn(client: Auth0Client, returnTo?: string): Promise<void> {
  return isSafeReturnTo(returnTo)
    ? client.loginWithRedirect({ appState: { returnTo } })
    : client.loginWithRedirect();
}

/**
 * True when the current URL looks like an Auth0 redirect callback — either a
 * success (`code` + `state`) or a failure (`error` + `state`, e.g.
 * `access_denied`). Both must be consumed: the success path completes the login,
 * the error path lets {@link completeRedirectCallback} surface the failure rather
 * than silently dropping it.
 */
export function hasRedirectCallbackParams(search: string = window.location.search): boolean {
  const params = new URLSearchParams(search);
  return params.has('state') && (params.has('code') || params.has('error'));
}

/** The Auth0 callback params stripped from the URL after the callback is handled. */
const AUTH_CALLBACK_PARAMS = ['code', 'state', 'error', 'error_description'] as const;

/**
 * Complete the Auth0 redirect callback, then strip ONLY the auth params
 * (`code`/`state`/`error`/`error_description`) from the URL so a refresh can't
 * replay a consumed authorization code — while preserving any other query params
 * and the hash fragment. On an Auth0 error redirect `handleRedirectCallback`
 * rejects; the URL is still cleaned (finally) and the rejection propagates to the
 * bootstrap so it can surface an error state. Call once on app load when
 * {@link hasRedirectCallbackParams} is true.
 *
 * Story 6.27 (ADO-1206) — the `handleRedirectCallback` result is no longer
 * discarded: its `appState.returnTo` (threaded at sign-in initiation, see
 * {@link signIn}) is returned so the bootstrap can navigate the router back to the
 * user's intended deep-link route instead of leaving them on the origin. A
 * missing / blank / unsafe `returnTo` returns `undefined` (→ origin landing,
 * unchanged from pre-6.27). The value is validated un-trusted via
 * {@link isSafeReturnTo} (open-redirect defence-in-depth). The auth-param URL
 * stripping (and its error-path `finally`) is unchanged.
 */
export async function completeRedirectCallback(client: Auth0Client): Promise<string | undefined> {
  try {
    const result = await client.handleRedirectCallback<{ returnTo?: unknown }>();
    const returnTo = result?.appState?.returnTo;
    return isSafeReturnTo(returnTo) ? returnTo : undefined;
  } finally {
    const url = new URL(window.location.href);
    for (const param of AUTH_CALLBACK_PARAMS) url.searchParams.delete(param);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }
}

/** Resolve the current access token (persisted cache; rotating refresh under the hood). */
export function getAccessToken(client: Auth0Client): Promise<string> {
  return client.getTokenSilently();
}

/**
 * Story 6.3 (ADO-879, AC1) — force a fresh access token, bypassing the SDK
 * cache so an expired token is replaced via the rotating refresh token
 * (`AUTH_FLOW_SPEC §8`). Used by the transport's post-401 retry. Rejects with an
 * Auth0 error when the refresh itself fails (see {@link isTerminalAuthError}).
 */
export function forceRefreshAccessToken(client: Auth0Client): Promise<string> {
  return client.getTokenSilently({ cacheMode: 'off' });
}

/**
 * Story 6.3 (ADO-879, AC3) — end the Auth0 session (the IdP session), then return
 * to the app origin where the shell re-bootstraps to the branded sign-in. NON-
 * federated by design — and COMPLETE under D9 (Story 6.14): there is no upstream
 * enterprise IdP session in Phase 1 (staff are custom-DB users), so `federated`
 * is intentionally omitted. `returnTo` must be in the Auth0 application's
 * Allowed Logout URLs.
 */
export function logout(client: Auth0Client): Promise<void> {
  return client.logout({ logoutParams: { returnTo: window.location.origin } });
}

/**
 * Story 6.3 (ADO-879, session restore) — eagerly warm an existing session on
 * app load. NOTE the true v2 contract (verified against the vendored SDK):
 * `checkSession()` NEVER rejects — it early-returns when the SDK's
 * is-authenticated cookie is absent and swallows any inner refresh error. It is
 * therefore a best-effort warm-up only; the bootstrap's token-servability probe
 * (AuthProvider, Bug #1192 review) is what actually decides authenticated
 * vs not. Kept as the Story 6.3 seam; with the persisted cache the rehydration
 * needs no iframe and no third-party cookies.
 */
export function restoreSession(client: Auth0Client): Promise<void> {
  return client.checkSession();
}

/**
 * Story 6.3 (ADO-879, AC4) — the ID-token claims (incl. the custom `shoplink/*`
 * namespace) used to expose non-authoritative session context to the SPA. The SPA
 * never trust-decides on these — `apps/api` re-checks every request (§3).
 */
export function getSessionClaims(client: Auth0Client): Promise<IdToken | undefined> {
  return client.getIdTokenClaims();
}

/** Whether the SPA currently holds an authenticated session. */
export function isAuthenticated(client: Auth0Client): Promise<boolean> {
  return client.isAuthenticated();
}

/**
 * Auth0 error codes (and the SDK's `MissingRefreshTokenError`) that mean the
 * refresh is TERMINAL — the session is genuinely gone or needs interactive
 * re-authentication, so the SPA must clear local state and redirect to Universal
 * Login (AC2). Other failures (network blip, `TimeoutError`, Auth0 outage) are
 * TRANSIENT: the 401 is surfaced without a forced logout, so a momentary glitch
 * does not sign the user out. This closes the deferred-work finding that the prior
 * `catch → null` could not distinguish the two.
 *
 * Confirmed against the auth0-spa-js contract for `getTokenSilently({ cacheMode:
 * 'off' })`: OAuth errors surface as a `GenericError` carrying the `error` code
 * (`login_required`/`consent_required`/`interaction_required` — the OIDC
 * prompt=none family), `mfa_required` surfaces as `MfaRequiredError` whose `error`
 * is `'mfa_required'`, and a missing refresh token surfaces as
 * `MissingRefreshTokenError`. `interaction_required` and `mfa_required` both
 * demand an interactive step the silent refresh cannot perform → terminal, so
 * they redirect rather than falling through to TRANSIENT and stranding the user
 * on repeated 401s (the exact AC2 stuck-state).
 */
const TERMINAL_REFRESH_ERROR_CODES: ReadonlySet<string> = new Set([
  'login_required',
  'missing_refresh_token',
  'invalid_grant',
  'consent_required',
  'interaction_required',
  'mfa_required',
]);

/** True when an error from a forced refresh means the session is terminally gone. */
export function isTerminalAuthError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const candidate = err as { error?: unknown; name?: unknown };
  if (typeof candidate.error === 'string' && TERMINAL_REFRESH_ERROR_CODES.has(candidate.error)) {
    return true;
  }
  return candidate.name === 'MissingRefreshTokenError';
}

// ── Story 6.4 (ADO-880) — first-login onboarding ────────────────────────────

// ── Story 6.24 (ADO-1203) — data-driven expired-ticket classifier seam ───────
//
// Replaces the Story 6.4 hard-coded English-only regex + fixed
// {access_denied, invalid_request} code gate, which was broken in BOTH
// directions (deferred-work.md, Story 6.4 reviews 2026-06-30):
//   - UNDER-MATCH: an English-only `error_description` regex + the fixed code set
//     dropped a genuinely-expired ticket from a NON-English tenant, or one Auth0
//     surfaced under a code outside that set (e.g. `unauthorized`) — it fell
//     through to the generic sign-in-failed screen with no re-request action.
//   - OVER-MATCH: a bare `/expired/` alternative routed ANY non-activation
//     `access_denied`/`invalid_request` whose description merely mentioned
//     "expired" (a denied consent, a rule-rejected login) to the password-
//     recovery surface, prompting a set-password link the user didn't need.
//
// Both directions need the SAME external input: the exact `error` /
// `error_description` the Deloitte-owned Auth0 tenant emits for an expired ticket
// vs. other causes. That is a tenant-config fact (OQ-6.24.1) we do NOT have and
// must NOT invent — so, following the "wire the seam, defer the value" precedent
// (Stories 6.19/6.20), the shapes become a small, injectable DATA TABLE that
// ships EMPTY until BWG/Deloitte confirm. All Auth0-shape knowledge stays
// confined to this module (`AUTH_FLOW_SPEC §10` / future `IIdentityProvider`).

/**
 * One expected Auth0 error shape for an EXPIRED / already-used set-password
 * (activation) ticket, as emitted by the Deloitte-owned Auth0 tenant. A match
 * requires BOTH the exact `error` code AND a NARROW, ticket-specific
 * `error_description` pattern — never a bare substring, never a bare code — so a
 * generic failure that merely mentions "expired" is not misclassified.
 */
export interface ActivationLinkErrorMatcher {
  /** The exact Auth0 `error` code (e.g. `access_denied`, `invalid_request`, `unauthorized`). */
  readonly code: string;
  /**
   * A NARROW `error_description` matcher scoped to the expired/used-link phrasing
   * the tenant actually emits (in every language it uses) — NOT a bare `/expired/`.
   */
  readonly descriptionPattern: RegExp;
}

/**
 * The confirmed expired-ticket error shapes for the Deloitte-owned Auth0 tenant.
 *
 * INTENTIONALLY EMPTY (OQ-6.24.1). The exact `error` / `error_description` values
 * Auth0 emits for an expired vs. an already-used vs. an other-cause ticket —
 * including any non-English phrasing and any code outside `{access_denied,
 * invalid_request}` (e.g. `unauthorized`) — are an external BWG/Deloitte
 * tenant-config fact we do NOT have and must NOT invent. Until this table is
 * confirmed and populated, {@link isActivationLinkError} is conservatively safe:
 * it classifies NOTHING, so it never misroutes a generic failure to the
 * password-recovery surface (over-match eliminated) and never guesses an expiry
 * from an unreliable English heuristic (under-match eliminated) — an unclassified
 * callback error falls through to the generic, still-recoverable sign-in-failed
 * surface (`AuthProvider` → `store.setError`). Populating this table (and only
 * then) re-enables the friendly `<ActivationLinkExpired>` recovery routing,
 * correctly, across every code and language the tenant uses.
 */
export const ACTIVATION_LINK_ERROR_MATCHERS: readonly ActivationLinkErrorMatcher[] = [];

/**
 * Story 6.4 (ADO-880, AC3) / Story 6.24 (ADO-1203) — classify a redirect-callback
 * error as an EXPIRED / already-used set-password (activation) link, distinct from
 * a generic auth failure. The caller (`AuthProvider`) routes a positive match to
 * the friendly `<ActivationLinkExpired>` recovery surface instead of the generic
 * error screen; a negative match (incl. every input while the default table is the
 * empty OQ-6.24.1 placeholder) falls through to that generic, recoverable screen.
 *
 * Data-driven: an input matches only when SOME `matchers` entry has both the exact
 * `error` code AND a `descriptionPattern` that tests true against the string
 * `error_description`. `matchers` is injectable (default = the module table) so the
 * confirmed shapes can be supplied — and tests can prove the fix — without touching
 * this logic or any call site.
 */
export function isActivationLinkError(
  err: unknown,
  matchers: readonly ActivationLinkErrorMatcher[] = ACTIVATION_LINK_ERROR_MATCHERS,
): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const candidate = err as { error?: unknown; error_description?: unknown };
  const { error, error_description: description } = candidate;
  if (typeof error !== 'string' || typeof description !== 'string') return false;
  return matchers.some(
    (m) => m.code === error && matchesDescription(m.descriptionPattern, description),
  );
}

/**
 * Test `description` against a matcher pattern WITHOUT stateful surprises. A
 * `descriptionPattern` is a shared, table-level `RegExp` reused across every
 * classification call; if one is authored with the global (`g`) or sticky (`y`)
 * flag, `RegExp.prototype.test` advances and persists `lastIndex` on that shared
 * object, so the same expired-ticket description would alternate true/false
 * between calls — silently re-introducing the very under-match this seam fixes.
 * The `RegExp` type cannot forbid those flags, so we neutralise them here: a
 * pattern with no `g`/`y` is used as-is (`.test` on it ignores `lastIndex`), and
 * one that has them is rebuilt once without them. Matching stays deterministic
 * regardless of how the OQ-6.24.1 table is eventually authored.
 */
function matchesDescription(pattern: RegExp, description: string): boolean {
  const statelessFlags = pattern.flags.replace(/[gy]/g, '');
  const stateless =
    statelessFlags === pattern.flags ? pattern : new RegExp(pattern.source, statelessFlags);
  return stateless.test(description);
}

/**
 * Story 6.25 (ADO-1204, AC1) — the timeout bounding the shared
 * `/dbconnections/change_password` POST core ({@link postChangePasswordTicket}).
 * Mirrors the bootstrap's `BOOTSTRAP_TIMEOUT_MS` (`AuthProvider.withTimeout`) so
 * the SPA's Auth0 network seams share one consistent robustness posture. Beyond
 * this bound the request is aborted and the fetch rejects, so a hung IdP
 * connection can never permanently latch `<ChangePassword>`'s `inFlight` guard.
 */
export const CHANGE_PASSWORD_TIMEOUT_MS = 15_000;

/**
 * Inputs for {@link requestPasswordResetEmail} — the Deloitte-owned DB connection
 * is required to send. Story 6.5 (ADO-881) renamed this from `ActivationLinkRequest`
 * to a connection-neutral name now that onboarding (6.4) and self-service reset
 * (6.5) share one seam; {@link ActivationLinkRequest} is kept as a type alias.
 */
export interface PasswordResetEmailRequest {
  readonly domain: string;
  readonly clientId: string;
  /** The Auth0 database connection name (Deloitte-owned config; required by the API). */
  readonly connection: string;
  readonly email: string;
}

/** @deprecated Story 6.5 — use {@link PasswordResetEmailRequest}. Kept so 6.4 call sites compile unchanged. */
export type ActivationLinkRequest = PasswordResetEmailRequest;

/**
 * The single source of truth for the Auth0 Authentication API
 * `POST /dbconnections/change_password` call shared by EVERY email-based
 * set-password / reset / change flow — Story 6.4 onboarding re-send, Story 6.5
 * self-service reset, and Story 6.6 authenticated change. The body is byte-for-byte
 * identical across all of them: `{ client_id, email, connection }`, no password,
 * EVER (the credential is set only on the Auth0-hosted page; AC4). Story 6.14
 * (ADO-1186, D2): no `organization` key — Auth0 Organizations are not used.
 *
 * This core only performs the POST and returns the raw {@link Response}; it does
 * NOT decide how to treat the outcome. The two public wrappers diverge there:
 *   - {@link requestPasswordResetEmail} swallows everything (enumeration-safe).
 *   - {@link requestPasswordChangeEmail} surfaces failure (outcome-aware, AC3).
 * Keeping the POST here means there is no duplication and all Auth0-shape knowledge
 * stays confined to this module (`AUTH_FLOW_SPEC §10` / future `IIdentityProvider`).
 *
 * `@auth0/auth0-spa-js` exposes no password-reset method, so this goes direct to
 * the Auth0 domain (NOT `/api/v1`); the bearer transport is intentionally not
 * involved.
 *
 * Story 6.25 (ADO-1204, AC1) — the POST is bounded by an `AbortController` +
 * timeout (below). A request that connects but never responds must not hang
 * forever: without a bound, `<ChangePassword>`'s synchronous `inFlight` guard —
 * released only in the `finally` of the awaited send — stays latched and the
 * button is permanently inert (only a page reload recovers). Bounding it here, in
 * the shared core, fixes ALL callers at once (`requestPasswordResetEmail`,
 * {@link requestPasswordChangeEmail}, {@link requestActivationLink}) without any
 * component change: the reset wrapper still swallows the abort (enumeration-safe),
 * the change wrapper still rejects on it (outcome-aware → the `inFlight` guard is
 * released and AC3's retryable error surface renders).
 */
function postChangePasswordTicket(req: PasswordResetEmailRequest): Promise<Response> {
  // Mirror the bootstrap's `withTimeout` idiom (AuthProvider `BOOTSTRAP_TIMEOUT_MS`),
  // but use an AbortController so the hung request is actually torn down (its
  // connection released) rather than merely abandoned. The timer is always cleared
  // on settle so a completed request never triggers a late abort.
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new DOMException('change_password request timed out', 'TimeoutError'));
  }, CHANGE_PASSWORD_TIMEOUT_MS);
  return fetch(`https://${req.domain}/dbconnections/change_password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: req.clientId,
      email: req.email,
      connection: req.connection,
    }),
    signal: controller.signal,
  }).finally(() => clearTimeout(timer));
}

/**
 * The ENUMERATION-SAFE email-based set-password / reset trigger (Story 6.4
 * onboarding + Story 6.5 self-service reset, FR2). Used on the LOGGED-OUT paths.
 *
 * ENUMERATION-SAFE BY DESIGN (NFR15 / OWASP): the endpoint already returns a
 * generic 200 regardless of whether the email exists, and this helper NEVER
 * rejects and NEVER returns the outcome — so a caller cannot branch on (and
 * thereby disclose) account existence. The credential is never handled here; only
 * an email + the public `client_id` leave the browser.
 */
export async function requestPasswordResetEmail(req: PasswordResetEmailRequest): Promise<void> {
  try {
    await postChangePasswordTicket(req);
  } catch {
    // Swallow: enumeration-safety requires an identical outcome whether the send
    // succeeds, fails, or the network is down. The recovery surface always shows
    // the same generic confirmation.
  }
}

/**
 * Story 6.6 (ADO-882, AC1/AC2/AC3) — the OUTCOME-AWARE change-password trigger for
 * the AUTHENTICATED session. Performs the SAME `POST /dbconnections/change_password`
 * over the shared {@link postChangePasswordTicket} core (same body — no password,
 * ever), but — unlike {@link requestPasswordResetEmail} — it REJECTS on a non-`ok`
 * response or a network/transport error so the caller can surface AC3's
 * initiation-failure message.
 *
 * Why a sibling and not the existing function: the reset helper deliberately
 * swallows all errors for enumeration-safety on the *logged-out* paths, and that
 * contract must stay intact. For an *authenticated* user the account is already
 * known, so enumeration-safety is moot and surfacing failure is the correct
 * behaviour (Dev Notes §"Design decision 1").
 */
export async function requestPasswordChangeEmail(req: PasswordResetEmailRequest): Promise<void> {
  // A network/transport error propagates as the thrown fetch rejection; a reachable
  // Auth0 that returns a non-2xx becomes an explicit throw — both drive AC3.
  const res = await postChangePasswordTicket(req);
  if (!res.ok) {
    throw new Error(`change_password request failed (${res.status})`);
  }
}

/**
 * Story 6.4 (ADO-880, AC3) onboarding alias for {@link requestPasswordResetEmail}.
 * The set-password re-request and the self-service reset are the SAME
 * `/dbconnections/change_password` call; this alias keeps 6.4's `ActivationLinkExpired`
 * + `makeRequestActivationLink` call sites working unchanged after the 6.5 rename.
 */
export const requestActivationLink = requestPasswordResetEmail;

// ── Story 6.6 (ADO-882) — federated-identity predicate (relocated from the
//    staff-token fixture; now drives PRODUCTION behaviour, not just tests) ─────

/**
 * Strategy segment (first `|`-delimited part of an Auth0 `sub`) emitted for an
 * upstream enterprise IdP. This MUST mirror the authoritative server allow-list
 * `FEDERATED_STRATEGIES` in `apps/api/tooling/auth0/verify-auth0.verify.ts`
 * (Story 4.2) — it is an Auth0 platform constant, not connection-specific, so SPA
 * and API agree on what "federated" means.
 *
 * Story 6.6 (ADO-882, Task 3.2) relocated this here from
 * `__fixtures__/staff-token.fixture.ts` because the "Change password" affordance
 * gates on it in production: a federated staff identity has NO Auth0 database
 * password, so a `change_password` ticket is meaningless for them.
 *
 * Story 6.14 (ADO-1186, D9): enterprise SAML federation is DESCOPED from Phase 1
 * — staff are custom-DB users (`sub = auth0|<uuid>`), so this predicate is
 * CORRECT-BUT-INERT: no Phase-1 token carries a federated `sub`, every staff
 * identity passes the §8b change-password gate below, and that is the DESIRED
 * behaviour (staff hold an Auth0-surfaced password). Kept — server-mirrored,
 * fail-safe — because federation returns post-Phase-1.
 */
export const FEDERATED_STRATEGIES = ['samlp', 'oidc', 'waad', 'adfs', 'pingfederate'] as const;

/**
 * True when `sub` evidences a federated enterprise connection (never `auth0|…`).
 *
 * Mirrors the structural rules of the server's `checkStaffFederationOrigin`
 * (Story 4.2): the `sub` must split into `<strategy>|<connection>|<user-id>` with
 * every segment non-empty, and `<strategy>` must be one of the enterprise
 * {@link FEDERATED_STRATEGIES} (a bare `samlp|` or database `auth0|…` is rejected).
 */
export function isFederatedSub(sub: string): boolean {
  const segments = sub.split('|');
  const [strategy, connection, userId] = segments;
  if (segments.length < 3 || !strategy || !connection || !userId) {
    return false;
  }
  return (FEDERATED_STRATEGIES as readonly string[]).includes(strategy);
}
